/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { WatchQueryFetchPolicy } from '@apollo/client'
import { PublicListingsGroup } from '@gql/PublicListingsGroup'

type ClientMethod = 'post' | 'get'
type GroupVariables = { groupId: string | None; mainOnly?: boolean; privateVisibility: boolean | None; sellerId: string | None }

export function useProductGroup(inp: { vars: () => None | GroupVariables; pricing: ProductPricingType; fetchPolicy?: WatchQueryFetchPolicy }) {
  const auth = useAuth()
  const toMethod = (i: GroupVariables | None): ClientMethod => (i?.privateVisibility ? 'post' : 'get')
  const initFetch = (clientId: ClientMethod) => {
    return useApolloWrap2<gen.PublicListingsGroupQuery, gen.PublicListingsGroupQueryVariables>(PublicListingsGroup)({
      clientId,
      fetchPolicy: () => inp.fetchPolicy,
      ui: (i) => (i ? helpers.arr(i.publicListingsGroup) : undefined),
      vars() {
        const input = inp.vars()
        if (clientId !== toMethod(input)) return
        if (!input?.groupId) return
        const allowed = !input.privateVisibility || auth.value.privatePricings?.some((i) => i.sellerId === input.sellerId)
        if (!allowed) return
        return {
          input: {
            ...(input.mainOnly && { mainOnly: true }),
            ...(input.privateVisibility && { private: true }),
            groupId: input.groupId,
          },
        }
      },
    })
  }

  const fetchMap: Partial<Record<ClientMethod, ReturnType<typeof initFetch>>> = {}
  const activeMethod = computed((): ClientMethod => toMethod(inp.vars()))

  watch(
    () => activeMethod.value,
    (method) => (fetchMap[method] ||= initFetch(method)),
    { immediate: true }
  )

  const products = computed(() => fetchMap[activeMethod.value]?.ui.value)
  const privateGetter = useCatalogPrivate(() => products.value, inp.pricing, inp.fetchPolicy)

  return {
    products: computed(() => {
      const value = products.value
      return value === undefined ? undefined : privateGetter.toBoxes(value)
    }),
  }
}

export function useProductGroupLoading<T>(inp: {
  vars: () => None | { value: None | T; groupId: string | None; variantAvailableCount: number | None; mainOnly?: boolean; privateVisibility: boolean | None; sellerId: string | None }
  ui: (main: T, index: number) => ProductListingBoxFragmentFull
  pricing: ProductPricingType
  fetchPolicy?: WatchQueryFetchPolicy
}) {
  const products = useProductGroup({
    pricing: inp.pricing,
    fetchPolicy: inp.fetchPolicy,
    vars: () => {
      const input = inp.vars()
      return { groupId: input?.groupId, mainOnly: input?.mainOnly, privateVisibility: input?.privateVisibility, sellerId: input?.sellerId }
    },
  })

  return computed((): ProductListingBoxFragmentFull[] => {
    const variantlList = products.products.value
    if (variantlList !== undefined) return variantlList
    const input = inp.vars()
    const variantAvailableCount = input?.variantAvailableCount
    const product = input?.value
    if (!product || !variantAvailableCount) return []
    return Array.from({ length: variantAvailableCount }, (_, index): ProductListingBoxFragmentFull => inp.ui(product, index))
  })
}
